import { type InitOptions } from "i18next";
import englishResource from "./en.json";
import spanishResource from "./es.json";
import {
	type SupportedLanguage,
	supportedLanguages,
} from "./supportedLanguages";

export { NumberParser } from "./NumberParser";
export { useLanguage } from "./useLanguage";

export { type SupportedLanguage, supportedLanguages };
export type LanguageResource = typeof englishResource;

const fallbackLanguage = "en" satisfies SupportedLanguage;

export const i18nextConfig = {
	resources: { en: englishResource, es: spanishResource } satisfies Record<
		SupportedLanguage,
		PartialLanguageResource
	>,
	fallbackLng: fallbackLanguage,
	supportedLngs: supportedLanguages,
	nonExplicitSupportedLngs: true,
	interpolation: {
		// React should be escaping itself before placing in the DOM.
		escapeValue: false,
	},
} as const satisfies InitOptions;

type PartialLanguageResource = {
	// Required application keys.
	[K1 in keyof LanguageResource]: {
		// Required section keys.
		[K2 in keyof LanguageResource[K1]]: {
			// Optional text keys.
			//
			// Other languages may not (yet) have all keys translated but it should
			// fall back to English safely. Ideally, we would have everything
			// translated and can verify it by comparing against `LanguageResource`
			// directly, but it won't always be the case in practice.
			[K3 in keyof LanguageResource[K1][K2]]?: LanguageResource[K1][K2][K3];
		};
	};
};

// A quick check that you can uncomment to verify that the Spanish resource
// doesn't have any missing properties. Once uncommented and saved, you can run
// `bun check:types` and should see an error like:
//
// > The types of '"web"["dashboard"]' are incompatible between these types.
// > Property '"exerciseOption_yes"' is missing in type...
//
// if the resource is missing any properties relative to the English version.
//
// spanishResource satisfies LanguageResource;
